import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { StatusesColors } from '@features/projects/statuses-colors';
import { StatusesTitles } from '@features/projects/statuses-titles';
import { TranslateService } from '@ngx-translate/core';
import { TimeService } from '@services/common/time.service';
import { ProjectDetailTooltipComponent } from '@shared/components/scheduler-calendar/project-detail-tooltip/project-detail-tooltip.component';
import { ProjectStatusesEnum } from '@shared/enums/project-statuses.enum';
import { addDays } from 'date-fns';
import { BehaviorSubject } from 'rxjs';

import { legacyParse } from '@date-fns/upgrade/v2';

@Component({
  selector: 'day-progresses',
  templateUrl: './day-progresses.component.html',
  styleUrls: ['./day-progresses.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DayProgressesComponent implements OnChanges {
  @Input() set progresses(progresses) {
    this.allProgresses = progresses;
  }
  @Input() maxProgressesLength: any = 2;
  @Input() day: any;
  @Input() week: any;
  @Input() daysObject: any;
  @Input() dayCellWidth: BehaviorSubject<number>;
  @Input() hoveredProgressId: BehaviorSubject<number>;
  @Input() hideActionBtns = false;

  @Output() maxProgressesLengthChanged = new EventEmitter();
  @Output() onProjectDeleted = new EventEmitter();
  @Output() emitWeekData = new EventEmitter();
  @Output() onProjectEdited = new EventEmitter();
  @Output() hover = new EventEmitter();
  @Output() emitExpandEvent = new EventEmitter();
  @Output() triggerDayClick = new EventEmitter();

  sortedProgresses = [];
  allProgresses = [];
  progressColor: string;
  selectedProgress = null;

  onDeleteAction(project): void {
    this.onProjectDeleted.next(project);
  }

  onEditAction(project): void {
    this.onProjectEdited.next(project);
  }

  changeProgressColor(): void {
    this.selectedProgress['clicked'] = false;
  }

  getTooltip(project): string {
    const { Client, Project, Subproject } = this._translateService.instant(['Client', 'Project', 'Subproject']);
    const notSetWord = this._translateService.instant('Not set');
    if (project.parent_id) {
      return `${Subproject}: ${project.name} (${project.display_id})
      ${Client}: ${project?.client?.name ? project?.client?.name : notSetWord}
      ${Project}: ${project.parent_project} (${project.parent_id})`;
    }

    return `${Project}: ${project.name} (${project.display_id})
          ${Client}: ${project?.client?.name ? project?.client?.name : notSetWord}`;
  }

  sortProgresses(progresses, day, week, hide?): any | boolean {
    function sortFunc(a, b) {
      if (a.index < b.index) {
        return -1;
      }
      if (a.index > b.index) {
        return 1;
      }
      return 0;
    }

    const newProgressesList = [...progresses.sort(sortFunc)];

    if (hide) {
      week.forEach((item) => {
        item['showMore'] = Math.abs(2 - item['progressesLength']);
        item['showAll'] = false;
        item['maxProgressesLength'] = 2;
      });
      return newProgressesList;
    }

    const progressesLength = progresses.length;
    day['progressesLength'] = progressesLength;

    if (!day?.showAll) {
      let maxLength = progressesLength;
      day['showMore'] = 0;
      const firstDayInWeek = week[0];
      let maxProgressesLength = this.maxProgressesLength;
      if (firstDayInWeek.hasOwnProperty('maxProgressesLength')) {
        maxProgressesLength = firstDayInWeek['maxProgressesLength'];
      }

      if (progressesLength > maxProgressesLength && !day?.showAll) {
        maxLength = maxProgressesLength;
        day['showMore'] = progressesLength - maxProgressesLength;
      }

      newProgressesList.length = maxLength;
    } else if (day['showMore'] !== 0) {
      day['showAll'] = false;
      day['showMore'] = 0;
    }

    this.emitExpandEvent.emit();

    return newProgressesList;
  }

  getStatusTooltipValue(item: string): string {
    return StatusesTitles[item];
  }

  getStatusColor(item: string): string {
    switch (item) {
      case ProjectStatusesEnum.InProgress:
        return StatusesColors.InProgress;
      case ProjectStatusesEnum.Finished:
        return StatusesColors.Finished;
      case ProjectStatusesEnum.BehindSchedule:
        return StatusesColors.BehindSchedule;
      case ProjectStatusesEnum.OverBudget:
        return StatusesColors.OverBudget;
    }
  }

  setProgressCellHover(project, index, e): void {
    const hoverDate = this._timeService.formatDate(addDays(legacyParse(project.a_start), index), 'YYYY-MM-DD');
    this.hover.emit(hoverDate);
    e.stopPropagation();
    e.preventDefault();
  }

  disableProgressCellHover() {
    this.hover.emit();
  }

  openTooltip(elem: ProjectDetailTooltipComponent, progress) {
    progress['clicked'] = true;
    this.selectedProgress = progress;
    elem.menuTrigger.openMenu();
    elem.open();
  }

  onEmptyClick(e): void {
    e.stopPropagation();
    this.triggerDayClick.next(e);
  }

  showAllProgresses(day, week, progresses): any {
    day['showAll'] = true;
    day['showMore'] = 0;
    week[0]['maxProgressesLength'] = progresses.length;
    this.maxProgressesLengthChanged.next(progresses.length);
    this.emitExpandEvent.emit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.sortedProgresses = this.sortProgresses(this.allProgresses, this.day, this.week);
  }

  constructor(
    private _translateService: TranslateService,
    private _timeService: TimeService
  ) {}
}
